import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { LogOut } from "lucide-react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", testId: "nav-dashboard" },
  { to: "/valuation", label: "Valuation", testId: "nav-valuation" },
  { to: "/crawler", label: "Crawler", testId: "nav-crawler" },
  { to: "/results", label: "Results", testId: "nav-results" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header
        className="border-b caa-hairline sticky top-0 z-30 bg-white/85 backdrop-blur"
        data-testid="app-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center gap-8">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="flex items-center shrink-0"
            data-testid="wordmark"
            aria-label="CAA home"
          >
            <Logo height={40} />
          </button>

          <nav className="hidden md:flex items-center gap-1 flex-1">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[color:var(--caa-teal-soft)] text-[color:var(--caa-teal-deep)]"
                      : "text-[color:var(--caa-muted)] hover:text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)]/40"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {user && (
              <>
                <div className="hidden sm:flex flex-col items-end leading-tight">
                  <span className="text-sm font-semibold text-[color:var(--caa-charcoal)]">
                    {user.name || user.email}
                  </span>
                  <span className="text-xs text-[color:var(--caa-muted)] capitalize">
                    {(user.role || "").replaceAll("_", " ")}
                  </span>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  className="rounded-lg border caa-hairline px-3 py-2 text-sm font-semibold text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)] hover:text-[color:var(--caa-teal-deep)] transition-colors inline-flex items-center gap-2"
                >
                  <LogOut size={15} />
                  <span className="hidden sm:inline">Log out</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="md:hidden border-t caa-hairline">
          <div className="max-w-[1400px] mx-auto px-3 py-2 flex gap-1 overflow-x-auto">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    isActive
                      ? "bg-[color:var(--caa-teal-soft)] text-[color:var(--caa-teal-deep)]"
                      : "text-[color:var(--caa-muted)]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
